import { BetLinkButton } from '../../../components/BetLinkButton';
import { LogoGenerator } from '../../../components/LogoGenerator';
import { BetPriceDirection } from './BetPriceDirection';
import { useState, useEffect } from 'react';
import { sportsbookColourMapping } from './SportsbookColourMapping';
import external_link_icon from '../../../assets/images/external_link_icon.svg';

export const BookAndLink = (props) => {
	const { link } = props;
	const { book, mobile, selected, betPrice, direction, isSharp, isLoading } = props;
	const [hoveredIndex, setHoveredIndex] = useState(null);
	const [isSmallViewport, setIsSmallViewport] = useState(window.innerWidth <= 1400);

	useEffect(() => {
		const handleResize = () => {
			setIsSmallViewport(window.innerWidth <= 1400);
		};
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const user = JSON.parse(window.localStorage.getItem('userdb'));
	const isOracle = user?.stripe_plan?.nickname?.includes('Oracle') && user?.stripe_plan.active&& user?.stripe_subscription !== null && user?.stripe_status !== 'deleted' && user.stripe_status !== 'past_due';

	let bk = book;

	if (typeof bk === 'object') {
		bk = book.join(',');
	}
	if (!bk) {
		bk = '';
	}

	// Remove duplicates when splitting by comma
	const uniqueBooks = bk ? [...new Set(bk.split(',').map(b => b.trim()))] : [];

	// Check if bet link exists and is valid
	const hasBetLink = !mobile && link !== '' && link !== undefined && book !== 'Betway (Alaska)' && book !== 'WagerWiz';
	const shouldShowBetLink = isOracle ? hasBetLink : hasBetLink;

	// For sharp column: limit icons based on viewport size
	// On small viewports (<=1400px): show only 2 icons to prevent row height expansion
	// On larger viewports: show up to 4 icons in 2x2 grid
	const maxSharpIcons = isSharp && !mobile && isSmallViewport ? 2 : 4;
	const displayBooks = isSharp && !mobile ? uniqueBooks.slice(0, maxSharpIcons) : uniqueBooks;
	const extraBooksCount = isSharp && !mobile && uniqueBooks.length > maxSharpIcons ? uniqueBooks.length - maxSharpIcons : 0;
	
	// For sharp column: disable bet links if there are multiple sharps
	const hasMultipleSharps = isSharp && !mobile && uniqueBooks.length > 1;
	const hasExactlyTwoSharps = isSharp && !mobile && uniqueBooks.length === 2;
	const hasThreeOrMoreSharps = isSharp && !mobile && uniqueBooks.length >= 3;
	const hasThreeOrFourSharps = isSharp && !mobile && uniqueBooks.length >= 3 && uniqueBooks.length <= 4;
	const hasFiveOrMoreSharps = isSharp && !mobile && uniqueBooks.length >= 5;

	return (
		<div className={`flex items-center ${isSharp && mobile ? 'gap-2' : 'justify-between'} w-full`} style={{ overflow: 'visible', minWidth: 0 }}>
			{/* Left side: Book logos, bet price and direction */}
			<div className="flex items-center gap-1">
				{/* Book logos with hover functionality (desktop only) */}
				{isSharp && !mobile ? (
					hasMultipleSharps ? (
						// Multiple sharps: responsive grid layout
						<div className="sharp-grid-container flex flex-row items-center flex-shrink-0 relative" style={{ gap: '6px', overflow: 'visible', alignItems: 'center' }}>
							{/* Grid container for icons */}
							<div className="flex flex-col" style={{ gap: hasExactlyTwoSharps ? '8px' : '6px', justifyContent: 'center' }}>
								{/* Desktop: 2x2 grid, Mobile/Small: 2 rows x 1 column */}
								<div className="sharp-grid-row flex items-center flex-wrap" style={{ gap: hasExactlyTwoSharps ? '8px' : (hasThreeOrMoreSharps ? '4px' : '6px') }}>
									{displayBooks.slice(0, 2).map((e, index) => (
										<div
											key={index}
											className="relative"
											style={{ position: 'relative', display: 'inline-block' }}
										>
											<LogoGenerator 
												sportsbook={e} 
												selected={selected}
												largeSize={false}
												// On smaller viewports, always use 50px (same as exactly 2 sharps) for consistent alignment
												smallLogos={isSmallViewport ? false : !hasExactlyTwoSharps}
												// Top row: 50px for 3-4 sharps (to match exactly 2), 35px for 5+ sharps, but always 50px on small viewports
												customSize={isSmallViewport ? 50 : (hasThreeOrFourSharps ? 50 : (hasFiveOrMoreSharps ? 35 : undefined))}
												hasBetLink={false}
												isHovered={false}
											/>
										</div>
									))}
								</div>
								{/* Bottom row: only show on larger viewports (not small viewports to prevent row height expansion) */}
								{displayBooks.length > 2 && !isSmallViewport && (
									<div className="sharp-grid-row flex items-center flex-wrap" style={{ gap: hasExactlyTwoSharps ? '8px' : (hasThreeOrMoreSharps ? '4px' : '6px') }}>
										{displayBooks.slice(2, 4).map((e, index) => {
											const actualIndex = index + 2;
											return (
												<div
													key={actualIndex}
													className="relative"
													style={{ position: 'relative', display: 'inline-block' }}
												>
													<LogoGenerator 
														sportsbook={e} 
														selected={selected}
														largeSize={false}
														smallLogos={hasFiveOrMoreSharps}
														// Bottom row: match top row - 50px for 3-4 sharps, 35px for 5+ sharps
														customSize={hasThreeOrFourSharps ? 50 : (hasFiveOrMoreSharps ? 35 : undefined)}
														hasBetLink={false}
														isHovered={false}
													/>
												</div>
											);
										})}
									</div>
								)}
							</div>
							{/* +X indicator - positioned to the right of the grid */}
							{extraBooksCount > 0 && (
								<div className="flex items-center justify-center flex-shrink-0" style={{ 
									// On smaller viewports, always use 3-4 sharps size (25px). Otherwise, use larger size for exactly 2 sharps
									width: isSmallViewport ? '25px' : (hasExactlyTwoSharps && displayBooks.length <= 2 ? '40px' : '25px'), 
									height: isSmallViewport ? '25px' : (hasExactlyTwoSharps && displayBooks.length <= 2 ? '40px' : '25px'),
									backgroundColor: '#363844',
									borderRadius: '6px',
									color: '#f0f2f6',
									fontSize: isSmallViewport ? '9px' : (hasExactlyTwoSharps && displayBooks.length <= 2 ? '11px' : '9px'),
									fontWeight: '600',
									alignSelf: 'center', // Always center vertically, whether 1 row or 2 rows
									margin: 'auto 0' // Additional centering to ensure consistent alignment
								}}>
									+{extraBooksCount}
								</div>
							)}
						</div>
					) : (
						// Single sharp: exactly like sportsbook column
						<div className="flex items-center gap-2 flex-shrink-0">
							{displayBooks.map((e, index) => {
								const isHovered = hoveredIndex === index;
								const showBetButton = shouldShowBetLink && isHovered && !mobile;
								
								return (
									<div
										key={index}
										className="relative"
										onMouseEnter={() => !mobile && setHoveredIndex(index)}
										onMouseLeave={() => !mobile && setHoveredIndex(null)}
										style={{ position: 'relative', display: 'inline-block', zIndex: 1 }}
									>
										<LogoGenerator 
											sportsbook={e} 
											selected={selected}
											largeSize={!mobile}
											hasBetLink={false}
											isHovered={isHovered && shouldShowBetLink}
										/>
										{/* Bet link icon - positioned outside Tooltip to avoid stacking context issues */}
										{shouldShowBetLink && !mobile && (() => {
											const badgeColor = sportsbookColourMapping[e] || sportsbookColourMapping[e?.trim()] || '#3b82f6';
											const rgb = badgeColor && badgeColor !== 'this is like 5 diff books' 
												? /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(badgeColor)
												: null;
											const glowColor = rgb 
												? `rgba(${parseInt(rgb[1], 16)}, ${parseInt(rgb[2], 16)}, ${parseInt(rgb[3], 16)}, 0.8)`
												: 'rgba(59, 130, 246, 0.8)';
											const iconBg = badgeColor && badgeColor !== 'this is like 5 diff books' 
												? badgeColor 
												: '#3b82f6';
											
											return (
												<div
													style={{
														position: 'absolute',
														bottom: '-4px',
														right: '-4px',
														width: '20px',
														height: '20px',
														background: iconBg,
														borderRadius: '50%',
														border: '2px solid rgba(255, 255, 255, 0.95)',
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'center',
														zIndex: 25,
														boxShadow: `0 0 8px ${glowColor}, 0 0 12px ${glowColor}, 0 2px 4px rgba(0, 0, 0, 0.4)`,
														pointerEvents: 'none',
													}}
												>
													<img 
														src={external_link_icon}
														alt="link"
														style={{
															width: '11px',
															height: '11px',
															filter: 'brightness(0) invert(1)',
														}}
													/>
												</div>
											);
										})()}
										{/* Bet button overlay on hover (desktop only) */}
										{showBetButton && (
											<div 
												className="absolute flex items-center justify-center z-10"
												style={{
													top: 0,
													left: 0,
													right: 0,
													bottom: 0,
													pointerEvents: 'auto',
													transition: 'all 0.3s ease',
													zIndex: 20,
												}}
											>
												{/* Backdrop for better visibility */}
												<div
													style={{
														position: 'absolute',
														inset: 0,
														background: 'rgba(0, 0, 0, 0.7)',
														borderRadius: '10px',
														backdropFilter: 'blur(4px)',
														zIndex: 1,
													}}
												/>
												<div style={{ position: 'relative', zIndex: 30 }}>
													<BetLinkButton mobile={mobile} link={link} size="large" sportsbook={e} />
												</div>
											</div>
										)}
									</div>
								);
							})}
						</div>
					)
				) : (
					// Regular sportsbook column: horizontal layout
					<div className="flex items-center gap-2 flex-shrink-0">
						{displayBooks.map((e, index) => {
							const isHovered = hoveredIndex === index;
							const showBetButton = shouldShowBetLink && isHovered && !mobile;
							
							return (
								<div
									key={index}
									className="relative"
									onMouseEnter={() => !mobile && setHoveredIndex(index)}
									onMouseLeave={() => !mobile && setHoveredIndex(null)}
									style={{ position: 'relative', display: 'inline-block', zIndex: 1 }}
								>
									<LogoGenerator 
										sportsbook={e} 
										selected={selected}
										largeSize={!mobile}
										hasBetLink={false}
										isHovered={isHovered && shouldShowBetLink}
									/>
									{/* Bet link icon - positioned outside Tooltip to avoid stacking context issues */}
									{shouldShowBetLink && !mobile && (() => {
										const badgeColor = sportsbookColourMapping[e] || sportsbookColourMapping[e?.trim()] || '#3b82f6';
										const rgb = badgeColor && badgeColor !== 'this is like 5 diff books' 
											? /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(badgeColor)
											: null;
										const glowColor = rgb 
											? `rgba(${parseInt(rgb[1], 16)}, ${parseInt(rgb[2], 16)}, ${parseInt(rgb[3], 16)}, 0.8)`
											: 'rgba(59, 130, 246, 0.8)';
										const iconBg = badgeColor && badgeColor !== 'this is like 5 diff books' 
											? badgeColor 
											: '#3b82f6';
										
										return (
											<div
												style={{
													position: 'absolute',
													bottom: '-4px',
													right: '-4px',
													width: '20px',
													height: '20px',
													background: iconBg,
													borderRadius: '50%',
													border: '2px solid rgba(255, 255, 255, 0.95)',
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													zIndex: 25,
													boxShadow: `0 0 8px ${glowColor}, 0 0 12px ${glowColor}, 0 2px 4px rgba(0, 0, 0, 0.4)`,
													pointerEvents: 'none',
												}}
											>
												<img 
													src={external_link_icon}
													alt="link"
													style={{
														width: '11px',
														height: '11px',
														filter: 'brightness(0) invert(1)',
													}}
												/>
											</div>
										);
									})()}
									{/* Bet button overlay on hover (desktop only) */}
									{showBetButton && (
										<div 
											className="absolute flex items-center justify-center z-10"
											style={{
												top: 0,
												left: 0,
												right: 0,
												bottom: 0,
												pointerEvents: 'auto',
												transition: 'all 0.3s ease',
												zIndex: 20,
											}}
										>
											{/* Backdrop for better visibility */}
											<div
												style={{
													position: 'absolute',
													inset: 0,
													background: 'rgba(0, 0, 0, 0.7)',
													borderRadius: '10px',
													backdropFilter: 'blur(4px)',
													zIndex: 1,
												}}
											/>
											<div style={{ position: 'relative', zIndex: 30 }}>
												<BetLinkButton mobile={mobile} link={link} size="large" sportsbook={e} />
											</div>
										</div>
									)}
								</div>
							);
						})}
					</div>
				)}
				
				{/* Bet price and direction */}
				{betPrice && (
					<div className="flex items-center gap-2">
						<span className="bg-gray-800 px-3 py-2 rounded-lg border border-gray-600 text-white font-medium text-sm h-8 flex items-center justify-center min-w-[60px] ml-2.5">
							{betPrice}
						</span>
						{direction !== null && (
							<div className="text-white">
								<BetPriceDirection direction={direction} isLoading={isLoading} />
							</div>
						)}
					</div>
				)}
			</div>
			
			{/* Right side: Bet button (mobile only) */}
			{!mobile && <div className="flex-shrink-0"></div>}
			{mobile && (
				<div className="flex-shrink-0">
					{isOracle && <>{book !== 'Betway (Alaska)' && <>{link !== '' && link !== undefined && book !== 'WagerWiz' && <BetLinkButton mobile={mobile} link={link} sportsbook={uniqueBooks[0]} />}</>}</>}
					{!isOracle && <>{book !== 'Betway (Alaska)' && <>{link !== '' && link !== undefined && book !== 'WagerWiz' && <BetLinkButton mobile={mobile} link={link} sportsbook={uniqueBooks[0]} />}</>}</>}
				</div>
			)}
		</div>
	);
};
